import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, DotsSixVertical, DotsThree, Eye, EyeSlash, Link as LinkIcon, Palette, PencilSimple, Plus, QrCode, Sparkle, Star, Trash, X } from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import { DndContext, PointerSensor, pointerWithin, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";
const btnPrimary = "h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1.5";
const btnGhost = "h-10 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-600 flex items-center gap-1.5";
const fmtP = (p) => `${(p || 0).toFixed(2).replace(".", ",")} €`;

const THEME_DEFAULTS = {
  logo_url: "", bg_color: "#fafafa", bg_image_url: "", overlay_color: "#000000",
  overlay_opacity: 0, button_color: "#ffffff", button_text_color: "",
  button_border_color: "#e4e4e7", button_radius: "tondi", button_shadow: "leggero",
  text_color: "#18181b", card_color: "", card_border_color: "",
  logo_size: 80, name_size: 24, menu_name_size: 12, category_text_color: "",
};
const RADII = { quadrati: 4, poco: 8, tondi: 16, pillola: 999 };
const OMBRE = { nessuno: "none", leggero: "0 1px 3px rgba(0,0,0,.10)", medio: "0 4px 12px rgba(0,0,0,.14)", forte: "0 10px 26px rgba(0,0,0,.22)" };

const buildTree = (list) => {
  const tops = list.filter((c) => !c.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return tops.map((c) => ({ ...c,
    children: list.filter((ch) => ch.parent_id === c.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) }));
};

function DragHandle({ attributes, listeners, testid }) {
  return (
    <button type="button" {...attributes} {...listeners} data-testid={testid}
      className="shrink-0 h-8 w-7 rounded-md text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing touch-none flex items-center justify-center">
      <DotsSixVertical size={16} weight="bold" />
    </button>
  );
}

function SortableRow({ id, container, className = "", children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, data: { container } });
  return (
    <div ref={setNodeRef} className={className}
      style={{ transform: CSS.Transform.toString(transform), transition,
               opacity: isDragging ? 0.55 : 1, position: "relative", zIndex: isDragging ? 20 : undefined }}>
      {children({ attributes, listeners })}
    </div>
  );
}

function ChildZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `zone-${id}`, data: { isContainer: true, container: id } });
  return (
    <div ref={setNodeRef} data-testid={`cat-child-zone-${id}`}
      className={`ml-9 mr-2 mb-2 rounded-lg border border-dashed px-2 py-1.5 space-y-1 transition-colors ${isOver ? "border-cyan-400 bg-cyan-50/60" : "border-zinc-200 bg-zinc-50/60"}`}>
      {children}
    </div>
  );
}

function ThemePreview({ theme }) {
  const ov = (() => {
    const h = (theme.overlay_color || "#000000").replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.min(0.9, Math.max(0, Number(theme.overlay_opacity) || 0))})`;
  })();
  const ovA = Math.min(0.9, Math.max(0, Number(theme.overlay_opacity) || 0));
  const btn = {
    background: theme.button_color || "#ffffff",
    color: theme.category_text_color || theme.button_text_color || "#18181b",
    border: `1.5px solid ${theme.button_border_color || "#e4e4e7"}`,
    borderRadius: RADII[theme.button_radius] ?? 16,
    boxShadow: OMBRE[theme.button_shadow] ?? OMBRE.leggero,
  };
  return (
    <div className="lg:sticky lg:top-4" data-testid="theme-preview">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Anteprima live</p>
      <div className="rounded-xl border border-zinc-200 overflow-hidden shadow-sm relative" style={{ backgroundColor: theme.bg_color }}>
        {theme.bg_image_url && (
          <div className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: `url(${theme.bg_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
        {ovA > 0 && <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: ov }} data-testid="tp-overlay-layer" />}
        <div className="relative z-10">
        <div className="pt-4 pb-2 text-center px-3">
          {theme.logo_url
            ? <img src={theme.logo_url} alt="" className="mx-auto object-contain" style={{ height: (theme.logo_size || 80) * 0.5 }} />
            : <div className="text-[10px] text-zinc-400 italic">logo</div>}
          <div className="font-bold mt-1" style={{ color: theme.text_color, fontSize: (theme.name_size || 24) * 0.6 }}>Nome Ristorante</div>
          <div style={{ color: theme.text_color, opacity: 0.6, fontSize: (theme.menu_name_size || 12) * 0.85 }}>Menu alla Carta</div>
        </div>
        <div className="px-3 pb-4 space-y-1.5">
          {["Antipasti", "Primi", "Secondi"].map((n) => (
            <div key={n} style={btn} className="px-3 py-2.5 text-xs font-bold flex items-center justify-between">
              {n}<span style={{ opacity: 0.4 }}>›</span>
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
}
function HexColor({ value, onChange, testid, allowAuto }) {
  const [text, setText] = useState((value || "").toLowerCase());
  useEffect(() => { setText((value || "").toLowerCase()); }, [value]);
  const norm = (v) => {
    let t = v.trim().toLowerCase();
    if (t && !t.startsWith("#")) t = `#${t}`;
    if (/^#[0-9a-f]{3}$/.test(t)) t = `#${t.slice(1).split("").map((c) => c + c).join("")}`;
    return /^#[0-9a-f]{6}$/.test(t) ? t : null;
  };
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={/^#[0-9a-f]{6}$/.test((value || "").toLowerCase()) ? value : "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 rounded-md border border-zinc-300 cursor-pointer shrink-0" data-testid={testid} />
      <input value={text} placeholder="#ffffff" spellCheck={false}
        onChange={(e) => { setText(e.target.value); const n = norm(e.target.value); if (n) onChange(n); }}
        onBlur={() => setText((value || "").toLowerCase())}
        className="h-10 w-24 px-2 rounded-md border border-zinc-300 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
        data-testid={`${testid}-hex`} />
      {allowAuto && (
        <button type="button" onClick={() => onChange("")} title="Usa colore automatico (brand)"
          className="text-[10px] text-zinc-400 underline">auto</button>
      )}
    </div>
  );
}

const RADII_OPTS = [["quadrati", "Quadrati"], ["poco", "Poco arrotondati"], ["tondi", "Tondi"], ["pillola", "Pillola"]];
const OMBRE_OPTS = [["nessuno", "Nessuno"], ["leggero", "Leggero"], ["medio", "Medio"], ["forte", "Forte"]];
const THEME_PRESETS = [
  { key: "minimal", label: "Minimal", swatch: "#fafafa", theme: { ...THEME_DEFAULTS } },
  { key: "elegante", label: "Elegante scuro", swatch: "#1a1a2e",
    theme: { ...THEME_DEFAULTS, bg_color: "#1a1a2e", button_color: "#23234a", button_text_color: "#f5f5f5",
             button_border_color: "#3d3d6b", button_radius: "pillola", button_shadow: "forte", text_color: "#f5f5f5" } },
  { key: "mare", label: "Mare", swatch: "#01B2E0",
    theme: { ...THEME_DEFAULTS, bg_color: "#e0f2f7", button_color: "#01B2E0", button_text_color: "#ffffff",
             button_border_color: "#01B2E0", button_radius: "pillola", button_shadow: "medio", text_color: "#0c4a5e" } },
  { key: "rustico", label: "Rustico", swatch: "#8b6f47",
    theme: { ...THEME_DEFAULTS, bg_color: "#f5efe6", button_color: "#fffdf8", button_text_color: "#5c4a32",
             button_border_color: "#d6c9b0", button_radius: "poco", button_shadow: "leggero", text_color: "#5c4a32" } },
];

function EyeBtn({ on, onToggle, testid }) {
  return (
    <button type="button" onClick={onToggle} data-testid={testid}
      title={on ? "Visibile ai clienti — clicca per nascondere" : "Nascosto ai clienti — clicca per mostrare"}
      className={on ? "text-emerald-600" : "text-rose-500"}>
      {on ? <Eye size={20} weight="bold" /> : <EyeSlash size={20} weight="bold" />}
    </button>
  );
}

function RowMenu({ id, children, onClose }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} data-testid={`more-${id}`}
        className="h-9 w-9 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-600">
        <DotsThree size={18} weight="bold" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-40"
          onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function Menu() {
  const [view, setView] = useState({ level: "menus" });
  const [rid, setRid] = useState(null);
  const [menus, setMenus] = useState(null);
  const [cats, setCats] = useState(null);
  const [dishes, setDishes] = useState(null);
  const [allergensData, setAllergensData] = useState({ allergens: [], labels: [] });

  const [menuForm, setMenuForm] = useState(null);      // {id?, name}
  const [catForm, setCatForm] = useState(null);        // {id?, name, description, image_url, visible, show_in_nav, tab}
  const [dishForm, setDishForm] = useState(null);      // {id?, name, price, ...}
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const photoRef = useRef(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [theme, setTheme] = useState(null);
  const [themeOpen, setThemeOpen] = useState(false);
  const [linkPop, setLinkPop] = useState(null);
  const [qrMenu, setQrMenu] = useState(null);
  const logoRef = useRef(null);
  const bgRef = useRef(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [bgAiPrompt, setBgAiPrompt] = useState("");
  const [bgBusy, setBgBusy] = useState(false);
  const [aiImpOpen, setAiImpOpen] = useState(false);
  const [aiImpUrl, setAiImpUrl] = useState("");
  const [aiImpBusy, setAiImpBusy] = useState(false);

  const aiImport = async () => {
    if (!aiImpUrl.trim()) { toast.error("Inserisci l'indirizzo della pagina menu"); return; }
    setAiImpBusy(true);
    try {
      const r = await api.post("/menu/ai-import", { url: aiImpUrl });
      toast.success(`Bozza importata: ${r.data.piatti} piatti in ${r.data.categorie} categorie. La trovi come menu non attivo: revisionala e attivala.`);
      setAiImpOpen(false);
      setAiImpUrl("");
      loadMenus();
    } catch (e) { toast.error(apiError(e)); } finally { setAiImpBusy(false); }
  };

  const loadMenus = useCallback(() => {
    api.get("/menu/menus").then((r) => setMenus(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  const loadCats = useCallback((menuId) => {
    api.get(`/menu/menus/${menuId}/categories`).then((r) => setCats(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  const loadDishes = useCallback((catId) => {
    api.get(`/menu/menu-categories/${catId}/dishes`).then((r) => setDishes(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    loadMenus();
    api.get("/menu/allergens").then((r) => setAllergensData(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setRid(r.data.restaurant_id)).catch(() => {});
    api.get("/settings").then((r) => setTheme({ ...THEME_DEFAULTS, ...(r.data.menu_theme || {}) })).catch(() => {});
  }, [loadMenus]);

  const saveTheme = async () => {
    setBusy(true);
    try {
      await api.patch("/settings", { menu_theme: theme });
      toast.success("Aspetto del menu salvato — aprilo dal link pubblico per vederlo");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const aiTheme = async () => {
    if (aiPrompt.trim().length < 3) { toast.error("Descrivi lo stile del menu in poche parole"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/menu/theme/ai", { prompt: aiPrompt.trim() });
      setTheme((t) => ({ ...t, ...r.data, logo_url: t.logo_url, bg_image_url: t.bg_image_url }));
      toast.success("Tema generato dall'IA — salva per renderlo attivo");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const logoRemoveBg = async () => {
    setLogoBusy(true);
    try {
      const r = await api.post("/menu/theme/logo-remove-bg", { logo_url: theme.logo_url });
      setTheme((t) => ({ ...t, logo_url: r.data.url }));
      toast.success("Sfondo del logo rimosso — salva per usare il nuovo logo");
    } catch (e) { toast.error(apiError(e)); } finally { setLogoBusy(false); }
  };

  const bgAiGenerate = async () => {
    const p = (bgAiPrompt || aiPrompt).trim();
    if (p.length < 3) { toast.error("Descrivi lo sfondo (es. texture legno chiaro, marmo scuro)"); return; }
    setBgBusy(true);
    try {
      const r = await api.post("/menu/theme/bg-generate", { prompt: p });
      setTheme((t) => ({ ...t, bg_image_url: r.data.url }));
      toast.success("Sfondo generato dall'IA — salva per renderlo attivo");
    } catch (e) { toast.error(apiError(e)); } finally { setBgBusy(false); }
  };

  const publicUrl = rid ? `${window.location.origin}/p/${rid}/menu` : "";
  const menuUrl = (m) => `${publicUrl}?m=${m.id}`;

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Link pubblico copiato"));
  };

  const downloadQr = () => {
    const canvas = document.querySelector("#menu-qr-canvas canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-menu-${(qrMenu?.name || "pubblico").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    a.click();
  };

  const uploadImage = async (file, done) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/marketing/uploads/image", fd);
      done(r.data.url);
      toast.success("Immagine caricata");
    } catch (e) { toast.error(apiError(e)); } finally { setImgUploading(false); }
  };

  // ---------- azioni menu ----------
  const saveMenu = async () => {
    if (!menuForm?.name?.trim()) { toast.error("Inserisci il nome del menu"); return; }
    setBusy(true);
    try {
      if (menuForm.id) await api.patch(`/menu/menus/${menuForm.id}`, { name: menuForm.name.trim(), item_label: menuForm.item_label || "piatti" });
      else await api.post("/menu/menus", { name: menuForm.name.trim(), item_label: menuForm.item_label || "piatti" });
      toast.success(menuForm.id ? "Menu rinominato" : "Menu creato");
      setMenuForm(null);
      loadMenus();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleMenu = async (m) => {
    await api.patch(`/menu/menus/${m.id}`, { active: !m.active }).then(loadMenus).catch((e) => toast.error(apiError(e)));
  };
  const deleteMenu = async (m) => {
    if (!window.confirm(`Eliminare il menu "${m.name}" con tutte le categorie e i piatti?`)) return;
    await api.delete(`/menu/menus/${m.id}`).then(() => { toast.success("Menu eliminato"); loadMenus(); })
      .catch((e) => toast.error(apiError(e)));
  };

  // ---------- azioni categoria ----------
  const saveCat = async () => {
    if (!catForm?.name?.trim()) { toast.error("Inserisci il nome della categoria"); return; }
    setBusy(true);
    const payload = { name: catForm.name.trim(), description: catForm.description || "",
      image_url: catForm.image_url || "", visible: !!catForm.visible, show_in_nav: !!catForm.show_in_nav,
      parent_id: catForm.parent_id || "" };
    try {
      if (catForm.id) await api.patch(`/menu/menu-categories/${catForm.id}`, payload);
      else await api.post(`/menu/menus/${view.menu.id}/categories`, payload);
      toast.success(catForm.id ? "Categoria aggiornata" : "Categoria creata");
      setCatForm(null);
      loadCats(view.menu.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleCat = async (c) => {
    await api.patch(`/menu/menu-categories/${c.id}`, { visible: !c.visible })
      .then(() => loadCats(view.menu.id)).catch((e) => toast.error(apiError(e)));
  };
  const deleteCat = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}" con tutti i suoi piatti?`)) return;
    await api.delete(`/menu/menu-categories/${c.id}`)
      .then(() => { toast.success("Categoria eliminata"); loadCats(view.menu.id); })
      .catch((e) => toast.error(apiError(e)));
  };

  // ---------- azioni piatto ----------
  const emptyDish = { name: "", price: "", description: "", allergens: [], labels: [],
    dish_of_day: false, variants: [], specs: [], photo: "" };
  const saveDish = async () => {
    if (!dishForm?.name?.trim()) { toast.error("Inserisci il nome del piatto"); return; }
    setBusy(true);
    const payload = { ...dishForm, name: dishForm.name.trim(),
      price: parseFloat(String(dishForm.price).replace(",", ".")) || 0,
      specs: (dishForm.specs || []).filter((s) => (s.label || "").trim() && (s.value || "").trim()),
      photo: dishForm.photo || null };
    try {
      if (dishForm.id) await api.patch(`/menu/dishes/${dishForm.id}`, payload);
      else await api.post(`/menu/menu-categories/${view.cat.id}/dishes`, payload);
      toast.success(dishForm.id ? "Piatto aggiornato" : "Piatto aggiunto");
      setDishForm(null);
      loadDishes(view.cat.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleDish = async (d) => {
    await api.patch(`/menu/dishes/${d.id}`, { available: !d.available })
      .then(() => loadDishes(view.cat.id)).catch((e) => toast.error(apiError(e)));
  };
  const deleteDish = async (d) => {
    if (!window.confirm(`Eliminare "${d.name}"?`)) return;
    await api.delete(`/menu/dishes/${d.id}`)
      .then(() => { toast.success("Piatto eliminato"); loadDishes(view.cat.id); })
      .catch((e) => toast.error(apiError(e)));
  };

  const toggleIn = (form, setForm, key, val) =>
    setForm({ ...form, [key]: (form[key] || []).includes(val)
      ? form[key].filter((x) => x !== val) : [...(form[key] || []), val] });

  // ---------- drag & drop: categorie/sottocategorie e piatti ----------
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [treeLocal, setTreeLocal] = useState(null);
  const [dishOrder, setDishOrder] = useState(null);
  useEffect(() => { if (cats) setTreeLocal(buildTree(cats)); }, [cats]);
  useEffect(() => { if (dishes) setDishOrder(dishes); }, [dishes]);

  const onCatDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id || !treeLocal) return;
    const src = active.data.current?.container || "root";
    const dst = over.data.current?.isContainer ? over.data.current.container : (over.data.current?.container ?? "root");
    const getList = (tree, cont) => (cont === "root" ? tree : tree.find((c) => c.id === cont)?.children || []);
    const found = getList(treeLocal, src).find((c) => c.id === active.id);
    if (!found || dst === found.id) return;
    if (dst !== "root" && (found.children || []).length) {
      toast.error("Sposta prima le sue sottocategorie: massimo due livelli"); return;
    }
    const moving = { ...found, children: [] };
    const next = treeLocal.map((c) => ({ ...c, children: [...(c.children || [])] }));
    const srcList = getList(next, src);
    const fromIdx = srcList.findIndex((c) => c.id === active.id);
    srcList.splice(fromIdx, 1);
    const dstList = getList(next, dst);
    const toIdx = over.data.current?.isContainer ? dstList.length
      : Math.max(0, dstList.findIndex((c) => c.id === over.id));
    dstList.splice(toIdx, 0, moving);
    setTreeLocal([...next]);
    const items = [];
    next.forEach((c, i) => {
      items.push({ id: c.id, order: i, parent_id: null });
      c.children.forEach((ch, j) => items.push({ id: ch.id, order: j, parent_id: c.id }));
    });
    try {
      await api.put(`/menu/menus/${view.menu.id}/categories/order`, { items });
      toast.success("Ordine salvato");
    } catch (e) { toast.error(apiError(e)); loadCats(view.menu.id); }
  };

  const onDishDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id || !dishOrder) return;
    const from = dishOrder.findIndex((d) => d.id === active.id);
    const to = dishOrder.findIndex((d) => d.id === over.id);
    if (from < 0 || to < 0) return;
    const nextList = arrayMove(dishOrder, from, to);
    setDishOrder(nextList);
    try {
      await api.put(`/menu/menu-categories/${view.cat.id}/dishes/order`, { ids: nextList.map((d) => d.id) });
      toast.success("Ordine salvato");
    } catch (e) { toast.error(apiError(e)); loadDishes(view.cat.id); }
  };

  // ---------- VISTA: elenco menu ----------
  if (view.level === "menus") {
    return (
      <div className="space-y-4" data-testid="menu-page">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Menu</h1>
            <p className="text-xs text-zinc-500">Crea più menu (es. Pranzo, Cena, Stagionale): il cliente li vede switchabili nel link pubblico</p>
          </div>
          <button onClick={() => setThemeOpen(!themeOpen)} data-testid="menu-theme-btn" className={btnGhost}>
            <Palette size={14} weight="bold" /> Personalizza aspetto
          </button>
          <button onClick={() => setAiImpOpen(!aiImpOpen)} data-testid="menu-ai-import-btn" className={btnGhost}>
            <Sparkle size={14} weight="fill" /> Importa dal sito con IA
          </button>
          <button onClick={() => setMenuForm({ name: "", item_label: "piatti" })} data-testid="menu-add-btn" className={btnPrimary}>
            <Plus size={14} weight="bold" /> Aggiungi menu
          </button>
        </div>

        {aiImpOpen && (
          <div className="bg-white border border-cyan-200 rounded-lg p-4" data-testid="menu-ai-import-panel">
            <h2 className="font-display font-bold text-zinc-900 mb-1 flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-cyan-600" /> Importa il menu dal tuo sito con l'IA</h2>
            <p className="text-xs text-zinc-500 mb-3">Incolla l'indirizzo della pagina menu del tuo sito: l'IA legge piatti, categorie e prezzi e crea una <strong>bozza non attiva</strong> da revisionare prima di pubblicarla.</p>
            <div className="flex flex-wrap gap-2">
              <input value={aiImpUrl} onChange={(e) => setAiImpUrl(e.target.value)} placeholder="www.iltuosito.it/menu"
                className={`${inputCls} flex-1 min-w-56`} data-testid="menu-ai-import-url" />
              <button onClick={aiImport} disabled={aiImpBusy} data-testid="menu-ai-import-go" className={btnPrimary}>
                {aiImpBusy ? "Lettura in corso…" : "Importa bozza"}
              </button>
            </div>
          </div>
        )}

        {themeOpen && theme && (
          <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="menu-theme-panel">
            <h2 className="font-display font-bold text-zinc-900 mb-3">Aspetto del menu pubblico</h2>
            <div className="grid lg:grid-cols-[1fr_280px] gap-5 items-start">
              <div className="space-y-4">

            <div>
              <label className={labelCls}>Preset — parti da una base pronta</label>
              <div className="flex gap-2 flex-wrap" data-testid="theme-presets">
                {THEME_PRESETS.map((p) => (
                  <button key={p.key} type="button"
                    onClick={() => { setTheme({ ...theme, ...p.theme, logo_url: theme.logo_url, bg_image_url: theme.bg_image_url }); toast.info(`Preset "${p.label}" applicato — salva per renderlo attivo`); }}
                    data-testid={`theme-preset-${p.key}`}
                    className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-xs font-bold text-zinc-700 flex items-center gap-2 hover:border-zinc-500 transition">
                    <span className="w-4 h-4 rounded-full border border-zinc-200" style={{ background: p.swatch }} />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Assistente IA — descrivi lo stile, l'IA crea il tema</label>
              <div className="flex gap-2 flex-wrap items-center">
                <div className="flex-1 min-w-60">
                  <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    className={inputCls} data-testid="theme-ai-prompt"
                    placeholder='Es. "trattoria toscana rustica" oppure "sushi bar elegante nero e oro"' />
                </div>
                <button type="button" onClick={aiTheme} disabled={aiBusy} data-testid="theme-ai-generate" className={btnGhost}>
                  <Sparkle size={14} weight="bold" /> {aiBusy ? "Generazione in corso..." : "Genera tema con IA"}
                </button>
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Logo e immagine di sfondo caricati restano invariati. Il tema si attiva dopo "Salva personalizzazione".</p>
            </div>

            <div>
              <label className={labelCls}>Logo ristorante (in alto nel menu)</label>
              {theme.logo_url ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <img src={theme.logo_url} alt="logo" className="h-14 object-contain rounded-md border border-zinc-200 bg-white p-1" data-testid="theme-logo-preview" />
                  <button type="button" onClick={logoRemoveBg} disabled={logoBusy} data-testid="theme-logo-transparent"
                    className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40">
                    <Sparkle size={13} weight="bold" /> {logoBusy ? "Rimozione sfondo..." : "Sfondo trasparente con IA"}
                  </button>
                  <button type="button" onClick={() => setTheme({ ...theme, logo_url: "" })} data-testid="theme-logo-remove"
                    className="h-9 px-3 rounded-md bg-white border border-rose-200 text-rose-600 text-xs font-semibold">
                    Rimuovi logo
                  </button>
                </div>
              ) : (
                <>
                  <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    data-testid="theme-logo-file"
                    onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setTheme((t) => ({ ...t, logo_url: url }))); e.target.value = ""; }} />
                  <button type="button" onClick={() => logoRef.current?.click()} disabled={imgUploading}
                    data-testid="theme-logo-upload" className={btnGhost}>
                    {imgUploading ? "Caricamento..." : "Carica logo (PNG/JPEG)"}
                  </button>
                </>
              )}
            </div>

            <div>
              <label className={labelCls}>Dimensioni logo e titoli</label>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="theme-sizes">
                {[["logo_size", "Logo", 40, 200, 5], ["name_size", "Nome ristorante", 16, 40, 1], ["menu_name_size", "Nome menù", 10, 24, 1]].map(([k, l, mn, mx, st]) => (
                  <div key={k} className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-semibold text-zinc-600">{l}</span>
                      <span className="text-[11px] font-mono text-zinc-400" data-testid={`theme-${k.replaceAll("_", "-")}-val`}>{theme[k] || THEME_DEFAULTS[k]}px</span>
                    </div>
                    <input type="range" min={mn} max={mx} step={st} value={theme[k] || THEME_DEFAULTS[k]}
                      onChange={(e) => setTheme({ ...theme, [k]: Number(e.target.value) })}
                      className="w-full accent-zinc-900" data-testid={`theme-${k.replaceAll("_", "-")}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Sfondo — colore</label>
                <HexColor value={theme.bg_color} onChange={(v) => setTheme({ ...theme, bg_color: v })} testid="theme-bg-color" />
              </div>
              <div>
                <label className={labelCls}>Sfondo — immagine</label>
                {theme.bg_image_url ? (
                  <div className="flex items-center gap-3">
                    <img src={theme.bg_image_url} alt="" className="h-10 w-16 object-cover rounded-md border border-zinc-200" data-testid="theme-bg-preview" />
                    <button type="button" onClick={() => setTheme({ ...theme, bg_image_url: "" })} data-testid="theme-bg-remove"
                      className="h-9 px-3 rounded-md bg-white border border-rose-200 text-rose-600 text-xs font-semibold">
                      Rimuovi immagine
                    </button>
                  </div>
                ) : (
                  <>
                    <input ref={bgRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      data-testid="theme-bg-file"
                      onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setTheme((t) => ({ ...t, bg_image_url: url }))); e.target.value = ""; }} />
                    <button type="button" onClick={() => bgRef.current?.click()} disabled={imgUploading}
                      data-testid="theme-bg-upload" className={btnGhost}>
                      {imgUploading ? "Caricamento..." : "Carica immagine di sfondo"}
                    </button>
                  </>
                )}
                <div className="flex gap-2 mt-2">
                  <input value={bgAiPrompt} onChange={(e) => setBgAiPrompt(e.target.value)}
                    className="flex-1 min-w-40 h-9 px-2 rounded-md border border-zinc-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    placeholder='Sfondo IA: es. "texture legno chiaro"' data-testid="theme-bg-ai-prompt" />
                  <button type="button" onClick={bgAiGenerate} disabled={bgBusy} data-testid="theme-bg-ai-generate"
                    className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-600 flex items-center gap-1.5 disabled:opacity-40 shrink-0">
                    <Sparkle size={13} weight="bold" /> {bgBusy ? "Generazione..." : "Genera con IA"}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>Overlay — colore</label>
                <HexColor value={theme.overlay_color} onChange={(v) => setTheme({ ...theme, overlay_color: v })} testid="theme-overlay-color" />
              </div>
              <div>
                <label className={labelCls}>Overlay — opacità ({Math.round((theme.overlay_opacity || 0) * 100)}%)</label>
                <input type="range" min="0" max="0.9" step="0.05" value={theme.overlay_opacity || 0}
                  onChange={(e) => setTheme({ ...theme, overlay_opacity: parseFloat(e.target.value) })}
                  className="w-full" data-testid="theme-overlay-opacity" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[["button_color", "Bottoni — sfondo"], ["button_text_color", "Bottoni — testo"],
                ["button_border_color", "Bottoni — bordo"], ["text_color", "Titoli — colore"],
                ["card_color", "Schede piatti — sfondo"], ["card_border_color", "Schede piatti — bordo"],
                ["category_text_color", "Categorie — testo"]].map(([key, label]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <HexColor value={theme[key]} onChange={(v) => setTheme({ ...theme, [key]: v })}
                    testid={`theme-${key.replace(/_/g, "-")}`} allowAuto />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Bottoni — angoli</label>
                <select value={theme.button_radius} onChange={(e) => setTheme({ ...theme, button_radius: e.target.value })}
                  className={inputCls} data-testid="theme-button-radius">
                  {RADII_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Bottoni — rilievo</label>
                <select value={theme.button_shadow} onChange={(e) => setTheme({ ...theme, button_shadow: e.target.value })}
                  className={inputCls} data-testid="theme-button-shadow">
                  {OMBRE_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={saveTheme} disabled={busy} data-testid="theme-save" className={btnPrimary}>Salva personalizzazione</button>
              <button onClick={() => setTheme({ ...THEME_DEFAULTS })} data-testid="theme-reset" className={btnGhost}>Ripristina default</button>
            </div>
              </div>
              <ThemePreview theme={theme} />
            </div>
          </div>
        )}

        {menuForm && (
          <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="menu-form">
            <div><label className={labelCls}>Nome menu *</label>
              <input value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })}
                className={inputCls} placeholder='Es. "Menu Pranzo"' data-testid="menu-form-name" /></div>
            <div><label className={labelCls}>Le voci di questo menu sono</label>
              <div className="flex gap-2" data-testid="menu-form-item-label">
                {[["piatti", "Piatti"], ["prodotti", "Prodotti"]].map(([k, l]) => (
                  <button type="button" key={k} onClick={() => setMenuForm({ ...menuForm, item_label: k })}
                    data-testid={`menu-form-item-${k}`}
                    className={`h-9 px-4 rounded-full text-xs font-bold border transition ${(menuForm.item_label || "piatti") === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">Per il menu food scegli "Piatti"; per drink e carta dei vini scegli "Prodotti".</p>
            </div>
            <div className="flex gap-2">
              <button onClick={saveMenu} disabled={busy} data-testid="menu-form-save" className={btnPrimary}>Salva</button>
              <button onClick={() => setMenuForm(null)} className={btnGhost}>Annulla</button>
            </div>
          </div>
        )}

        {!menus && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
        {menus?.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-12 text-center" data-testid="menu-empty">
            <p className="text-sm text-zinc-400">Nessun menu ancora. Clicca "Aggiungi menu" per iniziare.</p>
          </div>
        )}
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
          {menus?.map((m) => (
            <div key={m.id} className="p-3.5 flex items-center gap-3 flex-wrap" data-testid={`menu-row-${m.id}`}>
              <div className="flex-1 min-w-32">
                <span className="font-display font-bold text-zinc-900">{m.name}</span>
                <span className="text-[11px] text-zinc-400 ml-2">{m.categories_count} categorie · {m.dishes_count} {m.item_label === "prodotti" ? "prodotti" : "piatti"}</span>
              </div>
              <EyeBtn on={!!m.active} onToggle={() => toggleMenu(m)} testid={`menu-eye-${m.id}`} />
              <div className="relative">
                <button type="button" onClick={() => setLinkPop(linkPop === m.id ? null : m.id)} title="Link pubblico del menu" data-testid={`menu-link-${m.id}`}
                  className="h-9 w-9 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-600">
                  <LinkIcon size={16} weight="bold" />
                </button>
                {linkPop === m.id && (
                  <div className="absolute right-0 top-10 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg p-1.5 min-w-36" data-testid={`menu-linkpop-${m.id}`}>
                    <button onClick={() => { copyLink(menuUrl(m)); setLinkPop(null); }} data-testid={`menu-link-copy-${m.id}`}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 rounded-md flex items-center gap-2">
                      <LinkIcon size={13} /> Copia link
                    </button>
                    <button onClick={() => { window.open(menuUrl(m), "_blank"); setLinkPop(null); }} data-testid={`menu-link-open-${m.id}`}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 rounded-md flex items-center gap-2">
                      <Eye size={13} /> Apri link
                    </button>
                    <button onClick={() => { setQrMenu(m); setLinkPop(null); }} data-testid={`menu-link-qr-${m.id}`}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 rounded-md flex items-center gap-2">
                      <QrCode size={13} /> QR code
                    </button>
                  </div>
                )}
              </div>
              <button type="button" onClick={() => { setView({ level: "categories", menu: m }); setCats(null); loadCats(m.id); }}
                data-testid={`menu-open-${m.id}`} className={btnPrimary}>
                Apri
              </button>
              <RowMenu id={`menu-${m.id}`}>
                <button onClick={() => setMenuForm({ id: m.id, name: m.name, item_label: m.item_label || "piatti" })} data-testid={`menu-rename-${m.id}`}
                  className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 flex items-center gap-2">
                  <PencilSimple size={14} /> Rinomina
                </button>
                <button onClick={() => deleteMenu(m)} data-testid={`menu-delete-${m.id}`}
                  className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2">
                  <Trash size={14} /> Elimina
                </button>
              </RowMenu>
            </div>
          ))}
        </div>

        {qrMenu && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setQrMenu(null)} data-testid="qr-modal">
            <div className="bg-white rounded-xl p-6 w-full max-w-xs text-center space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display font-bold text-zinc-900">QR "{qrMenu.name}"</h3>
              <div id="menu-qr-canvas" className="flex justify-center">
                <QRCodeCanvas value={menuUrl(qrMenu)} size={512} marginSize={2} style={{ width: 220, height: 220 }} />
              </div>
              <p className="text-[11px] text-zinc-400 break-all">{menuUrl(qrMenu)}</p>
              <div className="flex gap-2 justify-center">
                <button onClick={downloadQr} data-testid="qr-download" className={btnPrimary}>Scarica PNG</button>
                <button onClick={() => setQrMenu(null)} data-testid="qr-close" className={btnGhost}>Chiudi</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- VISTA: categorie del menu ----------
  if (view.level === "categories") {
    const m = view.menu;
    const catRowInner = (c) => (
      <>
        <div className="flex-1 min-w-32 flex items-center gap-2">
          {c.image_url && <img src={c.image_url} alt="" className="h-9 w-12 object-cover rounded border border-zinc-200" />}
          <div>
            <span className="font-display font-bold text-zinc-900">{c.name}</span>
            <span className="text-[11px] text-zinc-400 ml-2">{c.dishes_count} {m.item_label === "prodotti" ? "prodotti" : "piatti"}</span>
          </div>
        </div>
        <EyeBtn on={!!c.visible} onToggle={() => toggleCat(c)} testid={`cat-eye-${c.id}`} />
        <button type="button" onClick={() => { setView({ level: "dishes", menu: m, cat: c }); setDishes(null); loadDishes(c.id); }}
          data-testid={`cat-open-${c.id}`} className={btnPrimary}>
          {m.item_label === "prodotti" ? "Prodotti" : "Piatti"}
        </button>
        <RowMenu id={`cat-${c.id}`}>
          <button onClick={() => setCatForm({ id: c.id, name: c.name, description: c.description || "",
            image_url: c.image_url || "", visible: !!c.visible, show_in_nav: c.show_in_nav !== false,
            parent_id: c.parent_id || "", tab: "dettagli" })}
            data-testid={`cat-edit-${c.id}`}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 flex items-center gap-2">
            <PencilSimple size={14} /> Modifica
          </button>
          <button onClick={() => deleteCat(c)} data-testid={`cat-delete-${c.id}`}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2">
            <Trash size={14} /> Elimina
          </button>
        </RowMenu>
      </>
    );
    return (
      <div className="space-y-4" data-testid="menu-categories-page">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <button onClick={() => { setView({ level: "menus" }); loadMenus(); }} data-testid="cat-back"
              className="text-xs font-semibold text-zinc-500 flex items-center gap-1 mb-1">
              <ArrowLeft size={13} /> Torna ai menu
            </button>
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Menu "{m.name}"</h1>
          </div>
          <button onClick={() => setCatForm({ name: "", description: "", image_url: "", visible: true, show_in_nav: true, parent_id: "", tab: "dettagli" })}
            data-testid="cat-add-btn" className={btnPrimary}>
            <Plus size={14} weight="bold" /> Aggiungi categoria
          </button>
        </div>

        {catForm && (
          <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="cat-form">
            <div className="flex gap-1 border-b border-zinc-100 pb-2">
              {[["dettagli", "Dettagli"], ["visibilita", "Visibilità"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setCatForm({ ...catForm, tab: k })} data-testid={`cat-tab-${k}`}
                  className={`h-8 px-3 rounded-md text-xs font-bold ${catForm.tab === k ? "bg-zinc-900 text-white" : "text-zinc-500"}`}>
                  {l}
                </button>
              ))}
            </div>
            {catForm.tab === "dettagli" && (
              <>
                <div><label className={labelCls}>Nome *</label>
                  <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                    className={inputCls} placeholder='Es. "Antipasti"' data-testid="cat-form-name" /></div>
                <div><label className={labelCls}>Categoria madre (vuoto = categoria principale)</label>
                  <select value={catForm.parent_id || ""} onChange={(e) => setCatForm({ ...catForm, parent_id: e.target.value })}
                    className={inputCls} data-testid="cat-form-parent">
                    <option value="">— Nessuna (categoria principale) —</option>
                    {(cats || []).filter((c) => !c.parent_id && c.id !== catForm.id).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select></div>
                <div><label className={labelCls}>Descrizione</label>
                  <textarea rows={3} value={catForm.description}
                    onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    data-testid="cat-form-desc" /></div>
                <div>
                  <label className={labelCls}>Immagine</label>
                  {catForm.image_url ? (
                    <div className="flex items-center gap-3">
                      <img src={catForm.image_url} alt="" className="h-16 w-24 object-cover rounded-md border border-zinc-200" data-testid="cat-form-image-preview" />
                      <button type="button" onClick={() => setCatForm({ ...catForm, image_url: "" })} data-testid="cat-form-image-remove"
                        className="h-9 px-3 rounded-md bg-white border border-rose-200 text-rose-600 text-xs font-semibold flex items-center gap-1.5">
                        <X size={13} /> Rimuovi immagine
                      </button>
                    </div>
                  ) : (
                    <>
                      <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        data-testid="cat-form-image-file"
                        onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setCatForm((f) => ({ ...f, image_url: url }))); e.target.value = ""; }} />
                      <button type="button" onClick={() => imgRef.current?.click()} disabled={imgUploading}
                        data-testid="cat-form-image-upload" className={btnGhost}>
                        {imgUploading ? "Caricamento..." : "Carica immagine (JPEG/PNG, max 5 MB)"}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
            {catForm.tab === "visibilita" && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input type="checkbox" checked={!!catForm.visible}
                    onChange={(e) => setCatForm({ ...catForm, visible: e.target.checked })}
                    data-testid="cat-form-visible" />
                  Visibile ai clienti nel menu pubblico
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input type="checkbox" checked={!!catForm.show_in_nav}
                    onChange={(e) => setCatForm({ ...catForm, show_in_nav: e.target.checked })}
                    data-testid="cat-form-nav" />
                  Mostra la categoria nella navigazione del menu
                </label>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={saveCat} disabled={busy} data-testid="cat-form-save" className={btnPrimary}>Salva</button>
              <button onClick={() => setCatForm(null)} className={btnGhost}>Annulla</button>
            </div>
          </div>
        )}

        {!cats && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
        {cats?.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-12 text-center" data-testid="cat-empty">
            <p className="text-sm text-zinc-400">Nessuna categoria. Aggiungi Antipasti, Primi, Secondi...</p>
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onCatDragEnd}>
          <SortableContext items={(treeLocal || []).map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="cat-tree">
              {treeLocal?.map((c) => (
                <SortableRow key={c.id} id={c.id} container="root">
                  {(hp) => (
                    <div>
                      <div className="p-3.5 flex items-center gap-2 flex-wrap" data-testid={`cat-row-${c.id}`}>
                        <DragHandle attributes={hp.attributes} listeners={hp.listeners} testid={`cat-drag-${c.id}`} />
                        {catRowInner(c)}
                      </div>
                      <ChildZone id={c.id}>
                        <SortableContext items={c.children.map((ch) => ch.id)} strategy={verticalListSortingStrategy}>
                          {c.children.map((ch) => (
                            <SortableRow key={ch.id} id={ch.id} container={c.id} className="bg-white rounded-md border border-zinc-100">
                              {(chp) => (
                                <div className="p-2.5 flex items-center gap-2 flex-wrap" data-testid={`cat-row-${ch.id}`}>
                                  <DragHandle attributes={chp.attributes} listeners={chp.listeners} testid={`cat-drag-${ch.id}`} />
                                  {catRowInner(ch)}
                                </div>
                              )}
                            </SortableRow>
                          ))}
                        </SortableContext>
                        {c.children.length === 0 && (
                          <p className="text-[10px] text-zinc-400 italic px-1 py-0.5">Trascina qui una categoria per trasformarla in sottocategoria</p>
                        )}
                      </ChildZone>
                    </div>
                  )}
                </SortableRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    );
  }

  // ---------- VISTA: piatti della categoria ----------
  const { menu: m, cat } = view;
  const noun = m.item_label === "prodotti" ? ["prodotto", "prodotti"] : ["piatto", "piatti"];
  return (
    <div className="space-y-4" data-testid="menu-dishes-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <button onClick={() => { setView({ level: "categories", menu: m }); loadCats(m.id); }} data-testid="dish-back"
            className="text-xs font-semibold text-zinc-500 flex items-center gap-1 mb-1">
            <ArrowLeft size={13} /> Torna alle categorie
          </button>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Categoria "{cat.name}"</h1>
        </div>
        <button onClick={() => setDishForm({ ...emptyDish })} data-testid="dish-add-btn" className={btnPrimary}>
          <Plus size={14} weight="bold" /> Aggiungi {noun[0]}
        </button>
      </div>

      {dishForm && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="dish-form">
          <div className="grid grid-cols-2 gap-2">
            <div><label className={labelCls}>Nome *</label>
              <input value={dishForm.name} onChange={(e) => setDishForm({ ...dishForm, name: e.target.value })}
                className={inputCls} data-testid="dish-form-name" /></div>
            <div><label className={labelCls}>Prezzo (€)</label>
              <input value={dishForm.price} onChange={(e) => setDishForm({ ...dishForm, price: e.target.value })}
                className={inputCls} placeholder="12,00" data-testid="dish-form-price" /></div>
          </div>
          <div><label className={labelCls}>Descrizione</label>
            <textarea rows={2} value={dishForm.description}
              onChange={(e) => setDishForm({ ...dishForm, description: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
              data-testid="dish-form-desc" /></div>
          <div>
            <label className={labelCls}>Scheda specifiche (es. Cantina, Vitigno, Vol %, Anno)</label>
            <div className="space-y-1.5" data-testid="dish-form-specs">
              {(dishForm.specs || []).map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={s.label} placeholder="Etichetta (es. Cantina)"
                    onChange={(e) => { const next = [...dishForm.specs]; next[i] = { ...next[i], label: e.target.value }; setDishForm({ ...dishForm, specs: next }); }}
                    className={inputCls} data-testid={`dish-spec-label-${i}`} />
                  <input value={s.value} placeholder="Valore (es. Montesole)"
                    onChange={(e) => { const next = [...dishForm.specs]; next[i] = { ...next[i], value: e.target.value }; setDishForm({ ...dishForm, specs: next }); }}
                    className={inputCls} data-testid={`dish-spec-value-${i}`} />
                  <button type="button" onClick={() => setDishForm({ ...dishForm, specs: dishForm.specs.filter((_, j) => j !== i) })}
                    data-testid={`dish-spec-del-${i}`}
                    className="shrink-0 h-9 w-9 rounded-md border border-zinc-200 text-zinc-400 hover:text-rose-600 flex items-center justify-center">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setDishForm({ ...dishForm, specs: [...(dishForm.specs || []), { label: "", value: "" }] })}
                data-testid="dish-spec-add"
                className="h-8 px-3 rounded-md bg-zinc-100 border border-zinc-200 text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
                <Plus size={12} weight="bold" /> Aggiungi specifica
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Allergeni (UE)</label>
            <div className="flex flex-wrap gap-1">
              {allergensData.allergens.map((a) => (
                <button type="button" key={a.key} onClick={() => toggleIn(dishForm, setDishForm, "allergens", a.key)}
                  data-testid={`dish-form-allergen-${a.key}`}
                  className={`h-7 px-2 rounded-full text-[11px] font-semibold border transition ${(dishForm.allergens || []).includes(a.key) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-zinc-600 border-zinc-300"}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Etichette</label>
            <div className="flex flex-wrap gap-1">
              {allergensData.labels.map((l) => (
                <button type="button" key={l.key} onClick={() => toggleIn(dishForm, setDishForm, "labels", l.key)}
                  data-testid={`dish-form-label-${l.key}`}
                  className={`h-7 px-2 rounded-full text-[11px] font-semibold border transition ${(dishForm.labels || []).includes(l.key) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-600 border-zinc-300"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={!!dishForm.dish_of_day}
                onChange={(e) => setDishForm({ ...dishForm, dish_of_day: e.target.checked })}
                data-testid="dish-form-dod" />
              <Star size={14} weight="fill" className="text-amber-500" /> Piatto del giorno
            </label>
            <div className="flex items-center gap-2">
              <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                data-testid="dish-form-photo-file"
                onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setDishForm((f) => ({ ...f, photo: url }))); e.target.value = ""; }} />
              <button type="button" onClick={() => photoRef.current?.click()} disabled={imgUploading}
                data-testid="dish-form-photo-upload" className={btnGhost}>
                {imgUploading ? "Caricamento..." : "Foto piatto"}
              </button>
              {dishForm.photo && (
                <>
                  <img src={dishForm.photo} alt="" className="h-9 w-12 object-cover rounded border border-zinc-200" />
                  <button type="button" onClick={() => setDishForm({ ...dishForm, photo: "" })} data-testid="dish-form-photo-remove"
                    className="text-rose-600 text-xs font-semibold">Rimuovi</button>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={saveDish} disabled={busy} data-testid="dish-form-save" className={btnPrimary}>Salva</button>
            <button onClick={() => setDishForm(null)} className={btnGhost}>Annulla</button>
          </div>
        </div>
      )}

      {!dishes && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
      {dishes?.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg py-12 text-center" data-testid="dish-empty">
          <p className="text-sm text-zinc-400">Nessun {noun[0]} in questa categoria.</p>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onDishDragEnd}>
        <SortableContext items={(dishOrder || []).map((d) => d.id)} strategy={verticalListSortingStrategy}>
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="dish-list">
            {dishOrder?.map((d) => (
              <SortableRow key={d.id} id={d.id} container="dishes">
                {(hp) => (
                  <div className="p-3.5 flex items-center gap-2 flex-wrap" data-testid={`dish-row-${d.id}`}>
                    <DragHandle attributes={hp.attributes} listeners={hp.listeners} testid={`dish-drag-${d.id}`} />
                    <div className="flex-1 min-w-32">
                      <span className="font-display font-bold text-zinc-900 flex items-center gap-1.5">
                        {d.name}
                        {d.dish_of_day && <Star size={13} weight="fill" className="text-amber-500" />}
                      </span>
                      {d.description && <p className="text-[11px] text-zinc-400 truncate">{d.description}</p>}
                    </div>
                    <span className="text-sm font-semibold text-zinc-700" data-testid={`dish-price-${d.id}`}>{fmtP(d.price)}</span>
                    <EyeBtn on={!!d.available} onToggle={() => toggleDish(d)} testid={`dish-eye-${d.id}`} />
                    <button type="button" onClick={() => setDishForm({ ...emptyDish, ...d, price: d.price })}
                      data-testid={`dish-edit-${d.id}`}
                      className="h-9 w-9 rounded-md bg-amber-500 text-white flex items-center justify-center">
                      <PencilSimple size={15} weight="bold" />
                    </button>
                    <button type="button" onClick={() => deleteDish(d)} data-testid={`dish-delete-${d.id}`}
                      className="h-9 w-9 rounded-md bg-rose-600 text-white flex items-center justify-center">
                      <Trash size={15} weight="bold" />
                    </button>
                  </div>
                )}
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
